import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MailWarning, Paperclip, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { Spinner } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { api, formatError } from "@/lib/api";
import { Field } from "@/pages/CompanyFormDialog";

const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
export const textToHtml = (t) => t.split(/\n\n+/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");

export function SafeModeNotice() {
  const { appSettings } = useAuth();
  if (!appSettings?.email_safe_mode) return null;
  return (
    <div className="flex gap-2 bg-amber-100 border border-amber-300 text-amber-900 rounded-md px-3 py-2 text-sm font-medium" data-testid="send-safe-mode-notice">
      <MailWarning className="h-4 w-4 mt-0.5 shrink-0" /> SAFE MODE — this will go to {appSettings.safe_mode_recipient} only
    </div>
  );
}

// Contract email: pick template -> merged preview, edit -> routing preview -> manual send.
export function ContractEmailDialog({ contract, initial, onClose, onSent }) {
  const lk = useLookups();
  const [templates, setTemplates] = useState([]);
  const [code, setCode] = useState(initial?.template_code || "");
  const [pte, setPte] = useState(contract.pte_company_id || "");
  const [form, setForm] = useState({ to: (initial?.to || []).join(", "), cc: (initial?.cc || []).join(", "), subject: initial?.subject || "", html: initial?.html || "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/email-templates", { params: { active: true, page_size: 100 } }).then((r) => setTemplates(r.data.items));
    api.get("/documents", { params: { contract_id: contract.id } }).then((r) => setDocs(r.data));
  }, [contract.id]);
  useEffect(() => {
    if (!code || (initial?.template_code === code && initial?.html)) return;
    api.post(`/contracts/${contract.id}/emails/render`, { template_code: code, pte_company_id: pte || null })
      .then(({ data }) => setForm({ to: data.to.join(", "), cc: data.cc.join(", "), subject: data.subject, html: data.html }))
      .catch((e) => toast.error(formatError(e)));
  }, [code, pte]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = () => ({ to: split(form.to), cc: split(form.cc), subject: form.subject, html: form.html, contract_id: contract.id,
    person_id: contract.contractor_id, template_code: code || null, chase_checklist_id: initial?.chase_checklist_id || null });
  const doPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post("/emails/preview", payload())).data); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/emails/send", payload());
      if (data.status === "sent") toast.success(`Email sent to ${data.actual_to.join(", ")}${data.safe_mode ? " (safe mode)" : ""}`);
      else toast.error(`Email ${data.status}: ${data.error || ""}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const tpl = templates.find((t) => t.code === code);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contract-email-dialog">
        <DialogHeader>
          <DialogTitle>{preview ? "Check and send" : "Send email"} · {contract.employment_id}</DialogTitle>
          <DialogDescription>Emails are only ever sent when you press Send.</DialogDescription>
        </DialogHeader>
        <SafeModeNotice />
        {!preview ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Template">
                <Select value={code || "__none"} onValueChange={(v) => setCode(v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">No template (blank)</SelectItem>{templates.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {code === "pte_company_details" && (
                <Field label="PTE company to merge">
                  <Select value={pte} onValueChange={setPte}>
                    <SelectTrigger data-testid="email-pte-select"><SelectValue placeholder="Choose PTE company" /></SelectTrigger>
                    <SelectContent>{toOpts(lk?.pte_companies).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            {tpl?.checklist_item_code && <p className="text-xs text-slate-500" data-testid="email-ticks-checklist">Sending this ticks the checklist item “{tpl.checklist_item_code}”.</p>}
            <Field label="To"><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="email-to-input" /></Field>
            <Field label="CC"><Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} data-testid="email-cc-input" /></Field>
            <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject-input" /></Field>
            <Field label="Message"><RichTextEditor value={form.html} onChange={(v) => setForm((f) => ({ ...f, html: v }))} testId="email-body-editor" /></Field>
            {docs.length > 0 && (
              <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="email-attachments-note"><Paperclip className="h-3 w-3" />
                Attachments aren't available with the built-in email service yet ({docs.length} contract document{docs.length > 1 ? "s" : ""} on file).</p>
            )}
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="email-preview">
            {preview.blocked.length > 0 && <div className="flex gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2" data-testid="email-blocked-note"><AlertTriangle className="h-4 w-4 mt-0.5" />Blocked recipient(s): {preview.blocked.join(", ")}. This email will not be sent.</div>}
            {preview.not_linked?.length > 0 && !preview.blocked.length && <div className="flex gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2" data-testid="email-notlinked-note"><AlertTriangle className="h-4 w-4 mt-0.5" />Not linked to this contract: {preview.not_linked.join(", ")}. Only the contractor, recruiter or company can be emailed.</div>}
            <p><span className="text-slate-400">Delivered to: </span><strong data-testid="email-preview-actual">{preview.actual.to.join(", ")}</strong>{preview.safe_mode && <span className="text-slate-400"> (originally {preview.original.to.join(", ")}{preview.original.cc.length ? `; CC ${preview.original.cc.join(", ")}` : ""})</span>}</p>
            <p><span className="text-slate-400">Subject: </span><strong data-testid="email-preview-subject">{preview.subject}</strong></p>
            <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-72 border border-slate-200 rounded-md bg-white" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {preview ? (
            <><Button variant="outline" onClick={() => setPreview(null)} data-testid="email-back-button">Back to edit</Button>
              <Button onClick={send} disabled={busy || !preview.will_send} data-testid="email-send-button">{busy ? <Spinner className="mr-2" /> : <Send className="h-4 w-4 mr-1" />}Send now</Button></>
          ) : (
            <><Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={doPreview} disabled={busy || !form.to.trim() || !form.subject.trim()} data-testid="email-preview-button">Preview</Button></>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
